// src/components/evaluation/DriverEvaluationDialog.js
import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Grid,
    Box,
    IconButton,
    Rating,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Button,
    TextField,
    TablePagination
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import ReactSpeedometer from 'react-d3-speedometer';
import PropTypes from 'prop-types';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import TWLocale from 'date-fns/locale/zh-TW';

const textColor = '#AAA';
const labels = {
    1: '極度危險',
    2: '稍危險',
    3: '尚可',
    4: '稍安全',
    5: '非常安全'
};

const DriverEvaluationDialog = ({ open, onClose, vehicleId, year, month }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [driverData, setDriverData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date(year, month - 1));
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(5);

    const fetchDriverData = async (queryYear, queryMonth) => {
        const defaultData = {
            identifier: vehicleId || '',
            driver_name: '',
            year: queryYear,
            month: queryMonth,
            monthly_avg_score: 0,
            monthly_avg_over_speed: 0,
            monthly_avg_acceleration: 0,
            monthly_avg_brake: 0,
            monthly_avg_turn: 0,
            daily_scores: []
        };
        try {
            setLoading(true);
            setError(null);
            const encodedVehicleId = encodeURIComponent(vehicleId);
            const response = await axios.get(
                `https://api.smartbustw.com/DriverScore/scores/vehicles/${encodedVehicleId}/scores/${queryYear}/${queryMonth}`
            );
            setDriverData(response.data);
        } catch (err) {
            setError('無法載入駕駛評分資料');
            setDriverData(defaultData);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
    };

    const handleQuery = () => {
        const queryYear = selectedDate.getFullYear();
        const queryMonth = selectedDate.getMonth() + 1;
        fetchDriverData(queryYear, queryMonth);
    };

    useEffect(() => {
        if (open && vehicleId) {
            fetchDriverData(year, month);
        }
    }, [open, vehicleId]);

    const evaluationStar = Math.round(driverData?.monthly_avg_score / 20);

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            </Dialog>
        );
    }


    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                駕駛評鑑詳情
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h5">
                                {driverData?.identifier} - {driverData?.driver_name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4} md={4}>
                            <LocalizationProvider locale={TWLocale} dateAdapter={AdapterDateFns}>
                                <DesktopDatePicker
                                    views={['year', 'month']}
                                    size="small"
                                    label="選擇查看月份"
                                    inputFormat="yyyy-MM"
                                    value={selectedDate}
                                    maxDate={new Date()}
                                    minDate={new Date(2020, 1, 17)}
                                    onChange={handleDateChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={2} md={2}>
                            <Button
                                sx={{ width: '100%', height: '56px' }}
                                variant="contained"
                                onClick={handleQuery}
                            >
                                查看
                            </Button>
                        </Grid>
                        <Grid item xs={12} textAlign="center">
                            <Typography variant="subtitle1" color="text.secondary">
                                {selectedDate.getFullYear()}年{selectedDate.getMonth() + 1}月評鑑報告
                            </Typography>
                        </Grid>

                        <Grid item xs={12} textAlign="center">
                            <Typography variant="h6">
                                月平均評等：
                                <Rating
                                    name="text-feedback"
                                    value={evaluationStar}
                                    readOnly
                                    precision={0.5}
                                    emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                                />
                                &ensp;
                                {labels[evaluationStar]}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <ReactSpeedometer
                                width={200}
                                height={150}
                                maxValue={100}
                                value={driverData?.monthly_avg_brake || 0}
                                valueFormat="d"
                                customSegmentStops={[0, 20, 40, 60, 80, 100]}
                                segmentColors={['#bf616a', '#d08770', '#ebcb8b', '#7CCD7C', '#a3be8c']}
                                currentValueText={`急煞車: ${driverData?.monthly_avg_brake?.toFixed(2)}分`}
                                textColor={textColor}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ReactSpeedometer
                                width={200}
                                height={150}
                                maxValue={100}
                                value={driverData?.monthly_avg_acceleration || 0}
                                valueFormat="d"
                                customSegmentStops={[0, 20, 40, 60, 80, 100]}
                                segmentColors={['#bf616a', '#d08770', '#ebcb8b', '#7CCD7C', '#a3be8c']}
                                currentValueText={`急加速: ${driverData?.monthly_avg_acceleration?.toFixed(2)}分`}
                                textColor={textColor}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ReactSpeedometer
                                width={200}
                                height={150}
                                maxValue={100}
                                value={driverData?.monthly_avg_turn || 0}
                                valueFormat="d"
                                customSegmentStops={[0, 20, 40, 60, 80, 100]}
                                segmentColors={['#bf616a', '#d08770', '#ebcb8b', '#7CCD7C', '#a3be8c']}
                                currentValueText={`急轉彎: ${driverData?.monthly_avg_turn?.toFixed(2)}分`}
                                textColor={textColor}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ReactSpeedometer
                                width={200}
                                height={150}
                                maxValue={100}
                                value={driverData?.monthly_avg_over_speed || 0}
                                valueFormat="d"
                                customSegmentStops={[0, 20, 40, 60, 80, 100]}
                                segmentColors={['#bf616a', '#d08770', '#ebcb8b', '#7CCD7C', '#a3be8c']}
                                currentValueText={`超速: ${driverData?.monthly_avg_over_speed?.toFixed(2)}分`}
                                textColor={textColor}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                每日評分記錄
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>日期</TableCell>
                                            <TableCell align="right">總分</TableCell>
                                            <TableCell>評等</TableCell>
                                            <TableCell align="right">超速</TableCell>
                                            <TableCell align="right">加速</TableCell>
                                            <TableCell align="right">煞車</TableCell>
                                            <TableCell align="right">轉彎</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {driverData?.daily_scores
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((score) => (
                                                <TableRow key={score.score_date}>
                                                    <TableCell>{new Date(score.score_date).toLocaleDateString()}</TableCell>
                                                    <TableCell align="right">{score.total_score.toFixed(2)}</TableCell>
                                                    <TableCell>{score.score_level}</TableCell>
                                                    <TableCell align="right">{score.over_speed_score.toFixed(2)}</TableCell>
                                                    <TableCell align="right">{score.acceleration_score.toFixed(2)}</TableCell>
                                                    <TableCell align="right">{score.brake_score.toFixed(2)}</TableCell>
                                                    <TableCell align="right">{score.turn_score.toFixed(2)}</TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                    <TablePagination
                                        rowsPerPageOptions={[10]}
                                        component="div"
                                        count={driverData?.daily_scores.length || 0}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(event, newPage) => setPage(newPage)}
                                        labelDisplayedRows={({ from, to, count }) =>
                                            `第 ${from}-${to} 筆，共 ${count} 筆`
                                        }
                                    />
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

DriverEvaluationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    vehicleId: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired
};

export default DriverEvaluationDialog;